import useMediaQuery from '@material-ui/core/useMediaQuery';
import logo from 'assets/branding/myHealthLogo.png';
import Svg from 'components/UI/Svg/Svg';
import { Color } from 'modules/styles/colors';
import React, { useEffect, useState } from 'react';
import Config from 'react-native-config';
import { GUEST_ROUTES } from 'screens/Guest/constants';
import { NavigationScreenProps } from 'screens/navigation';
import DeviceInfoServices from 'services/DeviceInfoServices';
import { dfdDefaultTheme, MuiBox, MuiGrid, MuiTypography } from 'theme/material-ui';
import { blurbs } from './constants';
import { LandingButton, LandingImageBackground, Logo } from './styled';
import { useDispatch } from 'react-redux';
import { handleLoginPress } from 'store/pageActions/bootstrap';
import { Spacing } from 'modules/styles/variables';
import ConfirmModal from 'components/UI/Modals/ConfirmModal';

const Blurb = ({
  icon,
  category,
  description,
  isSmallScreen
}: {
  icon: string;
  category: string;
  description: string;
  isSmallScreen: boolean;
}) => (
  <MuiBox
    mt={isSmallScreen ? 2 : 0}
    display="flex"
    flexDirection="column"
    alignItems="center"
    minWidth={150}
  >
    <MuiBox m={1} display="flex">
      {icon === 'SNavBilling' ? (
        <Svg
          name={icon}
          fill={Color.graySidebarIcon}
          stroke={Color.graySidebarIcon}
          height={100}
          width={100}
        />
      ) : (
        <Svg set="assets" fill="none" name={icon} height={100} width={100} />
      )}
    </MuiBox>
    <MuiBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
    >
      <MuiBox p={1}>
        <MuiTypography align="center" variant="h6">
          {category}
        </MuiTypography>
      </MuiBox>
      <MuiBox maxWidth={140}>
        <MuiTypography align="center" color={Color.grayHue3}>
          {description}
        </MuiTypography>
      </MuiBox>
    </MuiBox>
  </MuiBox>
);

export default function Landing({ history }: NavigationScreenProps) {
  const dispatch = useDispatch();
  const [showTimedOut, setShowTimedOut] = useState(false);

  const buildNumber = DeviceInfoServices.getBuildNumber();
  const isSmallScreen = useMediaQuery(dfdDefaultTheme.breakpoints.down('sm'));

  const handleRegistrationPress = () => {
    history.push(`/register`);
  };

  const handleGuestPress = () => {
    history.push(GUEST_ROUTES.GUEST_HOME);
  };

  useEffect(() => {
    const timedOut = window.localStorage.getItem('timedOut');
    if (timedOut) {
      setShowTimedOut(!!timedOut);
      window.localStorage.removeItem('timedOut');
    }
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://5rkyv87scfpx.statuspage.io/embed/script.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <LandingImageBackground isSmallScreen={isSmallScreen}>
      <ConfirmModal
        test-dataid="timeout-dialog"
        isOpen={showTimedOut}
        showCloseIcon
        icon="ExclamationCircle"
        title="Session Timeout"
        description="You have been logged out due to inactivity. Please log in again."
        confirmLabel="Sign in"
        onConfirm={() => setShowTimedOut(false)}
        fullWidth
        maxWidth={`${Spacing.xxLarge * 3}px`}
        position="bottom"
      />

      <MuiBox
        display="flex"
        flex="1 1 auto"
        justifyContent="center"
        flexWrap="wrap"
        alignItems="center"
        height="100%"
      >
        <MuiBox mt={8} width="100%" textAlign="center">
          <MuiTypography align="center" variant="h3" color={Color.primaryDark}>
            Welcome to
          </MuiTypography>

          <MuiBox p={2}>
            <Logo isSmallScreen={isSmallScreen} src={logo} alt="" />
          </MuiBox>

          <MuiBox mb={2}>
            <MuiTypography align="center" gutterBottom>
              Use your My Health login or create an account
            </MuiTypography>
          </MuiBox>

          <MuiBox my={2} maxWidth={275} mx="auto" mb={2}>
            <MuiGrid container spacing={2}>
              <MuiGrid item xs={12}>
                <LandingButton
                  data-testid="sign-in-button"
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => dispatch(handleLoginPress(history, 'landing'))}
                >
                  Sign in
                </LandingButton>
              </MuiGrid>
              <MuiGrid item xs={12}>
                <LandingButton
                  fullWidth
                  variant="contained"
                  color="default"
                  onClick={handleRegistrationPress}
                >
                  Create account
                </LandingButton>
              </MuiGrid>
              <MuiGrid item xs={12}>
                <LandingButton
                  fullWidth
                  variant="text"
                  color="primary"
                  onClick={handleGuestPress}
                  data-testid="continue-as-guest-button"
                >
                  Continue as guest
                </LandingButton>
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
          <MuiBox display="flex" justifyContent="center" flexWrap="wrap" mb={2}>
            {blurbs.map(item => (
              <Blurb
                key={item.category}
                icon={item.icon}
                category={item.category}
                description={item.description}
                isSmallScreen={isSmallScreen}
              />
            ))}
          </MuiBox>
        </MuiBox>

        <MuiBox p={2} alignSelf="center">
          <MuiTypography>
            {`© ${new Date().getFullYear()} Intermountain Healthcare, All rights reserved`}
            {Config.SHOW_APP_VERSION === 'enabled'
              ? ` Version: ${Config.APP_VERSION} Build: ${buildNumber} ${Config.BUILD_ENV}`
              : null}
          </MuiTypography>
        </MuiBox>
      </MuiBox>
    </LandingImageBackground>
  );
}
